/**
 * The three pills under a concept's title — ROUTE_INVENTORY §2.10 #41.
 *
 * Extracted from `ConceptDetailSidebar` because they are a colour SYSTEM, not
 * three unrelated badges, and a system is only reviewable when its members can
 * be seen side by side. Inside the sidebar each one needs a whole concept
 * payload to reach, and reaching all eight states means eight fetches.
 *
 * ## Everything is indigo, and the differences are deliberate
 *
 * The temptation with three adjacent pills is to give each its own hue, which
 * turns the top of the panel into a traffic light. Instead all three sit on the
 * brand ramp and encode their meaning differently:
 *
 * - **Level** is flat — it is a fact about the concept, not a judgement.
 * - **Importance** scales INTENSITY across four tiers, so `core` reads loudest
 *   and `peripheral` recedes. Only `core` earns the star.
 * - **Mastery** keeps the same indigo chip and carries its state in a small
 *   coloured dot, so "Struggling" is legible at a glance without a fourth
 *   competing pill colour.
 */

import { Star } from 'lucide-react';
import type { ImportanceTier, MasteryLevel } from '@/utils/mindspaceDisplay';
import { IMPORTANCE_TIER_LABEL } from '@/utils/mindspaceDisplay';

export const LEVEL_LABEL: Record<'foundational' | 'core' | 'advanced', string> =
  {
    foundational: 'Foundational',
    core: 'Core',
    advanced: 'Advanced',
  };

/*
 * Dark mode: the tint steps DOWN as the text steps UP. The chips were
 * `text-primary` on `bg-primary/30` and `/40` — lavender on lavender, 3.9:1
 * and 3.14:1 at 11px, failing axe the first time the drawer was rendered dark
 * (`App/Banners/Desktop/Layout/ConceptSidebar`). The louder tiers now take lighter
 * brand text on thinner tints (`/25` → `/15` → `/10` → `/5`), which keeps the
 * importance ramp's order and clears AA.
 */

/** Flat chip — a classification, carrying no ranking of its own. */
export function LevelPill({
  level,
}: {
  level: 'foundational' | 'core' | 'advanced';
}) {
  return (
    <span
      className='dark:tint-brand inline-flex items-center rounded-md bg-brand-50 px-1.5 py-0.5 text-xs font-medium text-brand-700'
      title='How advanced the material is: foundational comes first, then core, then advanced.'
    >
      {LEVEL_LABEL[level]}
    </span>
  );
}

// Importance intensity scales by tier. **The fill recedes; the text never
// does.** The first version faded both — `text-brand-600/80` on
// `bg-brand-50/70` for `supporting`, `text-brand-500/70` on `bg-brand-50/50`
// for `peripheral` — which measured 3.20:1 and 2.04:1 in light and 4.14:1 and
// 3.32:1 in dark. Three of those four fail WCAG AA, and the axe run inside the
// story gate caught it.
//
// It is the same defect, in the same shape, as the four Phase 1 fixes in
// `TagChipPicker` and `SourceLessonFilter`: subordinating a label by dropping
// its alpha makes the thing you still need to READ the thing you cannot read.
// Rank is carried by the fill and by font weight instead, both of which are
// chrome. Measured after: 8.19 / 6.56 / 6.21 / 5.86 in light, and 5.57 / 6.76
// for the two repaired tiers in dark.
const IMPORTANCE_TIER_STYLES: Record<
  ImportanceTier,
  { classes: string; showStar: boolean }
> = {
  core: {
    classes: 'bg-brand-100 font-semibold text-brand-800 dark:tint-brand-strong',
    showStar: true,
  },
  important: {
    classes: 'bg-brand-50 font-medium text-brand-700 dark:tint-brand',
    showStar: false,
  },
  supporting: {
    classes:
      'bg-brand-50/70 font-medium text-brand-700 dark:bg-primary/10 dark:text-primary',
    showStar: false,
  },
  peripheral: {
    classes:
      'bg-brand-50/40 font-normal text-brand-700 dark:bg-primary/5 dark:text-primary',
    showStar: false,
  },
};

export function ImportancePill({ tier }: { tier: ImportanceTier }) {
  const cfg = IMPORTANCE_TIER_STYLES[tier];
  return (
    // No `font-medium` in the base: each tier sets its own weight, which is
    // half of how the ramp reads now that alpha is off the table.
    <span
      className={`inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-xs ${cfg.classes}`}
      title='The LLM’s estimate of how central this concept is to the lessons that taught it.'
    >
      {cfg.showStar && <Star className='h-2.5 w-2.5 fill-current' />}
      {IMPORTANCE_TIER_LABEL[tier]}
    </span>
  );
}

const MASTERY_STYLES: Record<
  MasteryLevel,
  { label: string; dotClass: string }
> = {
  new: { label: 'Not studied yet', dotClass: 'bg-muted-foreground/50' },
  studied: {
    label: 'Studied, not assessed',
    dotClass: 'bg-blue-500 dark:bg-primary',
  },
  learning: { label: 'Learning', dotClass: 'bg-amber-500' },
  mastered: { label: 'Mastered', dotClass: 'bg-emerald-500' },
  struggling: { label: 'Struggling', dotClass: 'bg-rose-500' },
};

export function MasteryBadge({ level }: { level: MasteryLevel }) {
  const cfg = MASTERY_STYLES[level];
  return (
    <span className='dark:tint-brand inline-flex items-center gap-1 rounded-md bg-brand-50 px-1.5 py-0.5 text-xs font-medium text-brand-700'>
      {/* The dot is the ONLY thing separating the five mastery states, and it
          is colour with no text equivalent — so the label beside it has to
          carry the meaning on its own, and does. Removing the label and keeping
          the dot would fail WCAG 1.4.1 outright. */}
      <span
        className={`h-1.5 w-1.5 rounded-full ${cfg.dotClass}`}
        aria-hidden
      />
      {cfg.label}
    </span>
  );
}
